import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft, ShoppingCart, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
      } catch (_) {
        navigate("/app");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const addToCart = async () => {
    setAdding(true);
    try {
      await api.post("/cart/add", { product_id: id, quantity: 1 });
      toast.success("Ajouté au panier", { description: product?.name });
    } catch (e) {
      toast.error("Erreur", { description: "Impossible d'ajouter au panier" });
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-brand-green" />
      </div>
    );
  }
  if (!product) return null;

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div data-testid="product-detail-page">
      <Link to="/app" className="inline-flex items-center gap-2 text-sm text-brand-gray hover:text-white mb-6" data-testid="back-to-catalog">
        <ArrowLeft className="h-4 w-4" /> Catalogue
      </Link>
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="bg-brand-navy/30 border border-brand-blue/40 rounded-sm overflow-hidden aspect-square">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-brand-gray/40">Aucune image</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">
            {product.brand} · Grade {product.grade}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight">
            {product.name}
          </h1>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-black text-4xl text-white">{product.price.toFixed(2)} €</span>
            {product.original_price && (
              <>
                <span className="text-base line-through text-brand-gray/50">{product.original_price.toFixed(2)} €</span>
                <span className="px-2 py-1 bg-brand-green text-black text-xs font-bold rounded-sm">-{discount}%</span>
              </>
            )}
          </div>
          <p className="mt-6 text-brand-gray font-roboto leading-relaxed">{product.description}</p>

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mt-8 border border-brand-blue/40 rounded-sm divide-y divide-brand-blue/30">
              {Object.entries(product.specs).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span className="text-brand-gray/70 font-roboto uppercase text-xs tracking-wider">{k}</span>
                  <span className="font-semibold text-white">{String(v)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2 text-brand-gray">
              <ShieldCheck className="h-4 w-4 text-brand-green" /> Garantie 12 mois
            </div>
            <div className="flex items-center gap-2 text-brand-gray">
              <Truck className="h-4 w-4 text-brand-green" /> Livraison France
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={addToCart}
              disabled={adding || product.stock <= 0}
              data-testid="add-to-cart-button"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-sm bg-brand-green text-black font-bold hover:bg-brand-green/90 disabled:opacity-50 transition shadow-glow-green"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
              {product.stock > 0 ? "Ajouter au panier" : "Rupture de stock"}
            </button>
            <Link
              to="/app/cart"
              className="inline-flex items-center h-12 px-6 rounded-sm border border-brand-lightBlue/60 text-white hover:bg-brand-navy/40 transition"
              data-testid="go-to-cart-link"
            >
              Voir le panier
            </Link>
          </div>
          <div className="mt-3 text-xs text-brand-gray/70 font-roboto" data-testid="stock-info">
            Stock disponible : <span className="text-white font-bold">{product.stock}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
